import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { templatesApi, formatApiError } from "./api";
import { ArrowLeft, Trash2, FilePlus, BookmarkPlus, LogOut, Layers } from "lucide-react";

export default function AdminTemplates() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useFromId, setUseFromId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await templatesApi.list();
      setTemplates(data);
      setError(null);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar la plantilla "${name}"?`)) return;
    try {
      await templatesApi.remove(id);
      setTemplates((ts) => ts.filter((t) => t.id !== id));
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#E8C97A]" data-testid="back-dashboard-tpl">
            <ArrowLeft size={14} /> Volver
          </button>
          <div className="font-serif leading-tight text-center">
            <div className="text-base">Escapafy</div>
            <div className="italic text-[#E8C97A] text-[0.65rem] tracking-[0.18em] uppercase">Plantillas</div>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden md:inline text-sm text-white/70">{user?.email}</span>
            <button onClick={logout} className="text-white/70 hover:text-[#E8C97A] inline-flex items-center gap-2 text-sm">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <span className="label-eyebrow text-[#16335A]/70">Biblioteca</span>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl text-[#0A192F] tracking-tight">
            Plantillas de <span className="italic text-[#16335A]">propuestas</span>
          </h1>
          <p className="mt-3 text-[#3A506B] max-w-2xl">
            Guarda configuraciones reutilizables (bloques, hoteles, actividades, afiliados…) y crea nuevas propuestas en segundos.
            Para crear una plantilla, abre cualquier propuesta y pulsa <em>“Guardar plantilla”</em>.
          </p>
        </div>

        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 mb-6 text-sm">{error}</p>}

        {loading && <p className="text-[#3A506B] italic">Cargando...</p>}
        {!loading && templates.length === 0 && (
          <div className="bg-white border border-[#16335A]/10 p-12 text-center" style={{ borderRadius: "2px" }}>
            <Layers size={32} className="mx-auto text-[#16335A]/30" />
            <p className="mt-4 font-display text-2xl text-[#0A192F]">Aún no has guardado plantillas</p>
            <p className="mt-2 text-[#3A506B]">Crea una propuesta, configúrala a tu gusto y guárdala como plantilla para reutilizarla.</p>
            <Link to="/admin" className="mt-6 inline-flex items-center gap-2 bg-[#16335A] hover:bg-[#214A82] text-white px-6 py-3 rounded-full text-sm font-semibold">
              <FilePlus size={14} /> Ir al dashboard
            </Link>
          </div>
        )}

        {!loading && templates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {templates.map((t) => (
              <div
                key={t.id}
                data-testid={`template-card-${t.id}`}
                className="bg-white border border-[#16335A]/10 p-5 flex flex-col group hover:border-[#16335A]/30 transition-colors"
                style={{ borderRadius: "2px" }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#16335A]/10 flex items-center justify-center flex-shrink-0" style={{ borderRadius: "2px" }}>
                    <BookmarkPlus size={16} className="text-[#16335A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg text-[#0A192F] leading-tight">{t.name}</h3>
                    {t.destination && (
                      <p className="text-xs text-[#3A506B] mt-1 truncate">{t.destination}</p>
                    )}
                  </div>
                </div>
                {t.description && (
                  <p className="mt-3 text-sm text-[#3A506B] line-clamp-3">{t.description}</p>
                )}
                <p className="mt-3 text-[0.7rem] text-[#16335A]/60 uppercase tracking-wider">
                  {(t.blocks || []).length} bloques
                </p>
                <div className="mt-4 pt-4 border-t border-[#16335A]/10 flex justify-between items-center">
                  <button
                    onClick={() => setUseFromId(t.id)}
                    data-testid={`use-template-${t.id}`}
                    className="bg-[#16335A] hover:bg-[#214A82] text-white px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2"
                  >
                    <FilePlus size={12} /> Usar
                  </button>
                  <button
                    onClick={() => handleDelete(t.id, t.name)}
                    data-testid={`delete-template-${t.id}`}
                    className="text-red-600/70 hover:text-red-700"
                    title="Eliminar"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {useFromId && (
        <UseTemplateModal
          template={templates.find((x) => x.id === useFromId)}
          onClose={() => setUseFromId(null)}
          onCreated={(p) => navigate(`/admin/proposals/${p.id}`)}
        />
      )}
    </div>
  );
}

function UseTemplateModal({ template, onClose, onCreated }) {
  const [clientName, setClientName] = useState("");
  const [destination, setDestination] = useState(template?.destination || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const p = await templatesApi.use(template.id, {
        client_name: clientName.trim(),
        destination: destination.trim() || undefined,
      });
      onCreated(p);
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-md w-full p-8 space-y-5"
        style={{ borderRadius: "2px" }}
        data-testid="use-template-form"
      >
        <h2 className="font-display text-2xl text-[#0A192F]">Usar plantilla</h2>
        <p className="text-sm text-[#3A506B]">
          Crearás una nueva propuesta basada en <strong>{template?.name}</strong>.
        </p>
        <label className="block">
          <span className="block text-xs font-medium text-[#16335A] mb-2 uppercase tracking-wider">Cliente *</span>
          <input
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            required
            autoFocus
            data-testid="use-template-client"
            className="w-full px-4 py-3 bg-white border border-[#16335A]/20 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-[#16335A] mb-2 uppercase tracking-wider">Destino</span>
          <input
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            data-testid="use-template-destination"
            className="w-full px-4 py-3 bg-white border border-[#16335A]/20 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          />
        </label>
        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 text-sm">{error}</p>}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-3 text-sm text-[#3A506B] hover:text-[#16335A]">Cancelar</button>
          <button
            type="submit"
            disabled={loading || !clientName.trim()}
            data-testid="use-template-submit"
            className="bg-[#16335A] hover:bg-[#214A82] disabled:opacity-60 text-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2"
          >
            {loading ? "Creando..." : "Crear propuesta"}
          </button>
        </div>
      </form>
    </div>
  );
}
